// components/TraceSetupPanel.jsx — what the trace reads, and where.
//
//   • Sheets to scan: each sheet's role — assembly legend / floor plan / ceiling plan (RCP) / skip. The assembly
//     reader only reads legend sheets and the trace only runs on plan sheets, so a door schedule or an
//     elevation is never scanned by mistake. Roles are suggested from the sheet titles.
//   • Wall tag symbol: the outline the drafter draws wall-type tags in (diamond, hexagon, circle…). Only labels
//     in that outline are read as wall tags — door tags, room numbers and keynotes are left alone.
//   • Trace area: the part of THIS sheet to trace (zoom to the plan → "Use this view"). Without one the engine
//     finds the drawing itself (inside the border, title block / notes / legends / schedules left out).
//   • Line colours: walls drawn in a colour (fire-rating bands, pre-coloured assemblies) and what the legend says
//     each colour is — map a colour to a wall assembly and untagged walls in that colour take it.
//
// Settings live in traceSettings (saved with the takeoff):
//   sheetRoles: { [page]: "legend" | "floor" | "rcp" | "skip" }   tagSymbol: "auto" | "diamond" | …
//   drawAreas: { [page]: [[x0,y0,x1,y1], …] } (sheet units)        colorMap: { [family]: assemblyId }
import React, { useMemo, useState } from "react";

export const SHEET_ROLES = [
  { key: "", label: "—" },
  { key: "legend", label: "Assembly legend" },
  { key: "floor", label: "Floor plan" },
  { key: "rcp", label: "Ceiling plan (RCP)" },
  { key: "skip", label: "Skip" },
];
export const TAG_SYMBOLS = [
  { key: "auto", label: "Auto (the shape most of this job's tags use)" },
  { key: "diamond", label: "◇ Diamond" },
  { key: "hexagon", label: "⬡ Hexagon" },
  { key: "circle", label: "○ Circle" },
  { key: "square", label: "□ Square / box" },
  { key: "oval", label: "⬭ Oval / rounded box" },
  { key: "triangle", label: "△ Triangle" },
  { key: "none", label: "Plain text (no symbol)" },
  { key: "any", label: "Any label near a wall" },
];

/** a sheet's role from its title ("A0.04 WALL ASSEMBLY SCHEDULE" → legend, "A4.02 … REFLECTED CEILING PLAN" → rcp) */
export function suggestRole(title) {
  const t = String(title || "").toUpperCase();
  if (!t.trim() || /^SHEET \d+$/.test(t.trim())) return "";
  if (/DRAWING LIST|COVER (PAGE|SHEET)|SHEET INDEX|\bINDEX\b/.test(t))
    return "skip";
  if (
    /(WALL|PARTITION|CEILING|FLOOR|ROOF)\s*(AND\s+\w+\s*)?(TYPES?|ASSEMBL\w*|SCHEDULES?|LEGENDS?)|ASSEMBL(Y|IES)\s*(SCHEDULE|TYPES|LEGEND)?|PARTITION TYPES/.test(
      t,
    )
  )
    return "legend";
  if (/REFLECTED|\bR\.?C\.?P\.?\b|CEILING PLAN/.test(t)) return "rcp";
  if (
    /ELEVATION|SECTION|DETAIL|SCHEDULE|\bSITE\b|COVER|ROOF PLAN|DOOR|WINDOW|STAIR|MATRIX|SPECIFICATION|NOTES|DEMOLITION|DEMO\b|KEY PLAN|LANDSCAP|STRUCTURAL|FOUNDATION|FRAMING/.test(
      t,
    )
  )
    return "skip";
  if (/PLAN|FLOOR|LEVEL|BASEMENT|GROUND|MEZZANINE|SECTOR|UNIT\b|SUITE/.test(t))
    return "floor";
  return "";
}

/** the pages with a role (Number[]) */
export function pagesWithRole(settings, role) {
  return Object.entries(settings?.sheetRoles || {})
    .filter(([, r]) => r === role)
    .map(([n]) => Number(n))
    .sort((a, b) => a - b);
}

const box = "rounded border border-gray-700 p-2";
const head =
  "flex w-full items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500";
const sel =
  "min-w-0 rounded border border-gray-600 bg-gray-800 px-1 py-0.5 text-[11px] text-gray-200";
const btn =
  "rounded-md border border-gray-600 px-2 py-0.5 text-[11px] text-gray-200 hover:border-orange-500 hover:text-orange-300";

export default function TraceSetupPanel({
  pageCount = 0,
  pageNum,
  pageLabel = (n) => `Sheet ${n}`,
  excludedPages = [],
  settings,
  setSettings,
  info, // last engine read of this sheet: { tagStyle, colorLegend, colors, area }
  wallTypes = [], // [{ id, tag, name }]
  onUseView, // (add: boolean) → sets / adds the current view as this sheet's trace area
  onGoToPage,
  onSuggest, // async (overwrite) → reads every sheet's title block and sets the roles
  onOpenWizard, // opens Assemblies & trace (where the sheets are picked)
}) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({ tags: true, area: true, colors: true });
  const ts = settings || {};
  const roles = ts.sheetRoles || {};
  const set = (patch) => setSettings((prev) => ({ ...(prev || {}), ...patch }));
  const pages = useMemo(
    () =>
      Array.from({ length: pageCount }, (_, i) => i + 1).filter(
        (n) => !excludedPages.includes(n),
      ),
    [pageCount, excludedPages],
  );
  const setRole = (n, r) => {
    const next = { ...roles };
    if (r) next[n] = r;
    else delete next[n];
    set({ sheetRoles: next });
  };
  const suggestAll = async (overwrite) => {
    if (onSuggest) {
      setBusy(true);
      try {
        await onSuggest(overwrite);
      } finally {
        setBusy(false);
      }
      return;
    }
    const next = { ...roles };
    for (const n of pages) {
      if (!overwrite && next[n]) continue;
      const r = suggestRole(pageLabel(n));
      if (r) next[n] = r;
    }
    set({ sheetRoles: next });
  };
  const counts = { legend: 0, floor: 0, rcp: 0, skip: 0 };
  for (const n of pages) if (roles[n]) counts[roles[n]]++;
  const areas =
    (ts.drawAreas || {})[pageNum] ||
    (ts.drawAreas || {})[String(pageNum)] ||
    [];
  const clearArea = () => {
    const next = { ...(ts.drawAreas || {}) };
    delete next[pageNum];
    delete next[String(pageNum)];
    set({ drawAreas: next });
  };
  const tagStyle = info?.tagStyle;
  const legend = info?.colorLegend || [];
  // colours seen on walls (from the legend, or just found on the walls)
  const families = useMemo(() => {
    const m = new Map();
    for (const e of legend)
      m.set(e.family, { family: e.family, hex: e.hex, label: e.label });
    for (const c of info?.colors || [])
      if (!m.has(c.family))
        m.set(c.family, { family: c.family, hex: c.hex, label: "" });
    return [...m.values()];
  }, [legend, info?.colors]);
  const colorMap = ts.colorMap || {};

  return (
    <div className="space-y-3">
      {/* ── sheets: picked in one place — Assemblies & trace, step 1 */}
      <div className={box}>
        <div className={head}>
          <span>
            Sheets{" "}
            {counts.legend + counts.floor + counts.rcp
              ? `(${counts.legend} legend · ${counts.floor} floor · ${counts.rcp} RCP)`
              : "(none marked)"}
          </span>
        </div>
        <p className="mt-1.5 text-[10px] text-gray-500">
          {roles[pageNum] || roles[String(pageNum)]
            ? `This sheet is marked ${{ legend: "Legend", floor: "Floor plan", rcp: "RCP", skip: "Skip" }[roles[pageNum] || roles[String(pageNum)]]}.`
            : "This sheet isn't marked."}{" "}
          Legend / floor / RCP sheets are picked in Assemblies &amp; trace.
        </p>
        {onOpenWizard && (
          <button className={`${btn} mt-1.5`} onClick={onOpenWizard}>
            Pick sheets…
          </button>
        )}
      </div>

      {/* ── wall tag symbol */}
      <div className={box}>
        <button
          className={head}
          onClick={() => setOpen((o) => ({ ...o, tags: !o.tags }))}
        >
          <span>Wall tag symbol</span>
          <span>{open.tags ? "▾" : "▸"}</span>
        </button>
        {open.tags && (
          <div className="mt-2 space-y-1.5">
            <select
              value={ts.tagSymbol || "auto"}
              onChange={(e) => set({ tagSymbol: e.target.value })}
              className={`${sel} w-full`}
            >
              {TAG_SYMBOLS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-[11px] text-gray-300">
              <input
                type="checkbox"
                className="accent-orange-600"
                checked={ts.tagListOnly !== false}
                onChange={(e) => set({ tagListOnly: e.target.checked })}
              />
              Only this job's wall tags ({wallTypes.filter((a) => a.tag).length}{" "}
              from the assembly legend)
            </label>
            {tagStyle && (
              <p className="text-[10px] text-gray-500">
                {tagStyle.symbol
                  ? `Reading tags drawn in a ${tagStyle.symbol}${(ts.tagSymbol || "auto") === "auto" ? " (found on this sheet)" : ""}.`
                  : (ts.tagSymbol || "auto") === "auto"
                    ? "No single tag shape found on this sheet — every label near a wall counts."
                    : ""}
                {tagStyle.counts && Object.keys(tagStyle.counts).length > 0 && (
                  <>
                    {" "}
                    Seen:{" "}
                    {Object.entries(tagStyle.counts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, v]) => `${k} ${v}`)
                      .join(" · ")}
                  </>
                )}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── trace area */}
      <div className={box}>
        <button
          className={head}
          onClick={() => setOpen((o) => ({ ...o, area: !o.area }))}
        >
          <span>Trace area · this sheet</span>
          <span>{open.area ? "▾" : "▸"}</span>
        </button>
        {open.area && (
          <div className="mt-2 space-y-1.5">
            <p className="text-[10px] text-gray-500">
              {info?.engine
                ? `Engine ${info.engine}. `
                : "Engine: run Detect / Trace once to check the version. "}
              {areas.length
                ? `${areas.length} area${areas.length === 1 ? "" : "s"} set — only lines inside ${areas.length === 1 ? "it" : "them"} are traced.`
                : info?.area?.source === "auto"
                  ? `Automatic: inside the sheet border${info.area.titleBlock ? ", title block" : ""}${info.area.excluded ? ` and ${info.area.excluded} notes / legend / schedule box${info.area.excluded === 1 ? "" : "es"}` : ""} left out.`
                  : "Automatic: inside the sheet border, title block / notes / legends left out."}{" "}
              Zoom to one plan and use the view to trace only that.
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button className={btn} onClick={() => onUseView?.(false)}>
                Use this view
              </button>
              <button
                className={btn}
                onClick={() => onUseView?.(true)}
                title="A second plan on the same sheet"
              >
                Add this view
              </button>
              {areas.length > 0 && (
                <button className={btn} onClick={clearArea}>
                  Clear
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-gray-300">
              <input
                type="checkbox"
                className="accent-orange-600"
                checked={ts.gridLines !== false}
                onChange={(e) => set({ gridLines: e.target.checked })}
              />
              Column grid lines never cut rooms
            </label>
          </div>
        )}
      </div>

      {/* ── line colours */}
      {families.length > 0 && (
        <div className={box}>
          <button
            className={head}
            onClick={() => setOpen((o) => ({ ...o, colors: !o.colors }))}
          >
            <span>Line colours ({families.length})</span>
            <span>{open.colors ? "▾" : "▸"}</span>
          </button>
          {open.colors && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] text-gray-500">
                Walls drawn in a colour carry it on their line (and its legend
                meaning). Map a colour to an assembly and untagged walls in that
                colour take it.
              </p>
              {families.map((f) => (
                <div key={f.family} className="flex items-center gap-2">
                  <span
                    className="h-3 w-5 shrink-0 rounded-sm"
                    style={{ background: f.hex }}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-[11px] text-gray-300"
                    title={f.label || f.family}
                  >
                    {f.label || f.family}
                  </span>
                  <select
                    value={colorMap[f.family] || ""}
                    onChange={(e) =>
                      set({
                        colorMap: {
                          ...colorMap,
                          [f.family]: e.target.value || undefined,
                        },
                      })
                    }
                    className={`${sel} w-28`}
                  >
                    <option value="">— tags only</option>
                    {wallTypes.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.tag ? `${a.tag} — ` : ""}
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
